import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    quote: "Axomind reframed how my family thinks about wealth. The plan is rigorous, the communication is calm, and the outcomes speak for themselves.",
    name: "Rohan Mehta",
    role: "Founder, F&B Group",
    location: "Mumbai",
  },
  {
    quote: "What stood out is the discipline. They don't chase markets — they build a portfolio you can actually live with through volatility.",
    name: "Priya Sharma",
    role: "Senior Director, Tech",
    location: "Bengaluru",
  },
  {
    quote: "From SIPs to ELSS to retirement — everything is consolidated into one transparent dashboard. Genuinely a premium experience.",
    name: "Arvind Iyer",
    role: "Chartered Accountant",
    location: "Pune",
  },
  {
    quote: "Their HNI portfolio services are best-in-class. Quarterly reviews are sharp, data-driven and jargon-free.",
    name: "Sneha Kapoor",
    role: "NRI Investor",
    location: "Dubai",
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((v) => (v + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const go = (dir) => setIdx((v) => (v + dir + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[idx];

  return (
    <section data-testid="testimonials-section" className="section relative bg-[#050505]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Client Voices</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-white mt-4">
            Words from people we
            <span className="text-gold-gradient"> serve.</span>
          </h2>
        </div>

        <div className="relative glass rounded-3xl p-8 md:p-14 overflow-hidden">
          <Quote className="absolute top-6 right-8 text-[#D4AF37]/15" size={120} />
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              data-testid="testimonial-active"
            >
              <div className="flex gap-0.5 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="font-display text-2xl md:text-3xl text-white leading-snug">"{t.quote}"</p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-black font-bold font-display">
                  {t.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-sm text-gray-400">{t.role} • {t.location}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-10 bg-[#D4AF37]" : "w-2 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => go(-1)}
                data-testid="testimonial-prev"
                className="w-11 h-11 rounded-full glass flex items-center justify-center hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="testimonial-next"
                className="w-11 h-11 rounded-full glass flex items-center justify-center hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
